import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/ui/phone-input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Plus, UserCircle, Calendar, Save, Edit, Shield, Eye, FileText, Settings, Users, CreditCard, Dumbbell, Trash2, RotateCcw, ChevronDown, ChevronRight, Clock, TrendingUp, MoreVertical } from 'lucide-react';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { TrainerOrderManager } from '@/components/staff/TrainerOrderManager';
import { EmployeeOnboardingInviteButton } from '@/components/staff/EmployeeOnboardingInviteButton';
import { EmployeeMasterDataButton } from '@/components/staff/EmployeeMasterDataButton';
import { EmployeeInviteResendButton } from '@/components/staff/EmployeeInviteResendButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default permissions for new trainers
const DEFAULT_PERMISSIONS = {
  can_view_calendar: true,
  can_edit_own_appointments: true,
  can_edit_all_appointments: false,
  can_view_customers: false,
  can_view_own_customers: true,
  can_edit_customers: false,
  can_view_health_notes: false,
  can_view_finances: false,
  can_create_invoices: false,
  can_view_reports: false,
  can_view_own_reports: true,
  can_view_packages: false,
  can_sell_packages: false,
  can_view_settings: false,
  can_edit_settings: false,
  // Neue Berechtigungen
  can_view_training: true,
  can_view_nutrition: true,
  can_view_waitlist: true,
  can_view_resources: true,
  // Stundenerfassung Berechtigungen
  can_view_own_timetracking: true,
  can_view_all_timetracking: false,
  can_edit_own_timetracking: true,
  can_edit_all_timetracking: false,
};

// Permission labels in German
const PERMISSION_LABELS = {
  can_view_calendar: { label: 'Kalender anzeigen', icon: Calendar, category: 'Kalender' },
  can_edit_own_appointments: { label: 'Eigene Termine bearbeiten', icon: Calendar, category: 'Kalender' },
  can_edit_all_appointments: { label: 'Alle Termine bearbeiten', icon: Calendar, category: 'Kalender' },
  can_view_waitlist: { label: 'Warteliste sehen', icon: Calendar, category: 'Kalender' },
  can_view_customers: { label: 'Alle Kunden sehen', icon: Users, category: 'Kunden' },
  can_view_own_customers: { label: 'Eigene Kunden sehen', icon: Users, category: 'Kunden' },
  can_edit_customers: { label: 'Kunden bearbeiten', icon: Users, category: 'Kunden' },
  can_view_health_notes: { label: 'Gesundheits-Notizen', icon: Users, category: 'Kunden' },
  can_view_training: { label: 'Training sehen', icon: Users, category: 'Training & Ernährung' },
  can_view_nutrition: { label: 'Ernährung sehen', icon: Users, category: 'Training & Ernährung' },
  can_view_finances: { label: 'Finanzen einsehen', icon: CreditCard, category: 'Finanzen' },
  can_create_invoices: { label: 'Rechnungen erstellen', icon: FileText, category: 'Finanzen' },
  can_view_reports: { label: 'Alle Berichte sehen', icon: Eye, category: 'Berichte' },
  can_view_own_reports: { label: 'Eigene Statistiken sehen', icon: Eye, category: 'Berichte' },
  can_view_packages: { label: 'Pakete sehen', icon: CreditCard, category: 'Pakete' },
  can_sell_packages: { label: 'Pakete verkaufen', icon: CreditCard, category: 'Pakete' },
  can_view_resources: { label: 'Ressourcen sehen', icon: Settings, category: 'System' },
  can_view_settings: { label: 'Einstellungen sehen', icon: Settings, category: 'System' },
  can_edit_settings: { label: 'Einstellungen ändern', icon: Settings, category: 'System' },
  // Stundenerfassung
  can_view_own_timetracking: { label: 'Eigene Stunden sehen', icon: Calendar, category: 'Stundenerfassung' },
  can_view_all_timetracking: { label: 'Alle Stunden sehen', icon: Calendar, category: 'Stundenerfassung' },
  can_edit_own_timetracking: { label: 'Eigene Stunden bearbeiten', icon: Calendar, category: 'Stundenerfassung' },
  can_edit_all_timetracking: { label: 'Alle Stunden bearbeiten', icon: Calendar, category: 'Stundenerfassung' },
};

const StaffPage = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [staff, setStaff] = useState([]);
  const [services, setServices] = useState([]); // NEW: For service selection
  const [rooms, setRooms] = useState([]); // Räume für Mieter-Auswahl
  const [servicesLoaded, setServicesLoaded] = useState(false);
  const [roomsLoaded, setRoomsLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [availabilityDialogOpen, setAvailabilityDialogOpen] = useState(false);
  const [availHistory, setAvailHistory] = useState([]);
  const [showAvailHistory, setShowAvailHistory] = useState(false);
  const [vacationDialogOpen, setVacationDialogOpen] = useState(false); // NEW
  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false); // NEW: Permissions dialog
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [selectedPermissions, setSelectedPermissions] = useState(DEFAULT_PERMISSIONS);
  const [vacations, setVacations] = useState([]); // NEW
  const [credentialsDialogOpen, setCredentialsDialogOpen] = useState(false); // Show login credentials after creation
  const [createdCredentials, setCreatedCredentials] = useState(null); // Store created user credentials
  const [showArchived, setShowArchived] = useState(false); // Toggle: archivierte Trainer anzeigen
  const [vacationFormData, setVacationFormData] = useState({ // NEW
    start_date: '',
    end_date: '',
    notes: ''
  });
  const [editFormData, setEditFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    role: 'trainer',
    new_password: '', // NEU: Passwort-Feld für Änderung
    // Mieter-spezifische Felder
    rent_amount: '',
    rent_payment_interval: 'monthly',
    rented_room: '',
    rent_start_date: '',
    rent_first_billing_month: '',  // Erster Abrechnungsmonat (YYYY-MM Format)
    rent_auto_invoice: true,  // true = automatisch, false = manuell freigeben
    street: '',
    postal_code: '',
    city: '',
  });
  const [availability, setAvailability] = useState({
    monday: { active: true, slot_duration: '30min', slots: [{ from_time: '09:00', to_time: '18:00', service_ids: [], is_customer_visible: true }] },
    tuesday: { active: true, slot_duration: '30min', slots: [{ from_time: '09:00', to_time: '18:00', service_ids: [], is_customer_visible: true }] },
    wednesday: { active: true, slot_duration: '30min', slots: [{ from_time: '09:00', to_time: '18:00', service_ids: [], is_customer_visible: true }] },
    thursday: { active: true, slot_duration: '30min', slots: [{ from_time: '09:00', to_time: '18:00', service_ids: [], is_customer_visible: true }] },
    friday: { active: true, slot_duration: '30min', slots: [{ from_time: '09:00', to_time: '18:00', service_ids: [], is_customer_visible: true }] },
    saturday: { active: false, slot_duration: '30min', slots: [{ from_time: '10:00', to_time: '14:00', service_ids: [], is_customer_visible: true }] },
    sunday: { active: false, slot_duration: '30min', slots: [{ from_time: '10:00', to_time: '14:00', service_ids: [], is_customer_visible: true }] },
    color: '#3B82F6',
    special_dates: {}
  });
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'trainer',
    phone: '',
    studio_ids: [],
    // Mieter-spezifische Felder (nur relevant bei Rolle 'mieter')
    rent_amount: '',
    rent_payment_interval: 'monthly',
    rented_room: '',
    rent_start_date: '',
    rent_first_billing_month: '',
    rent_auto_invoice: true,
    // Adresse (für QR-Rechnung "Zahlbar durch")
    street: '',
    postal_code: '',
    city: '',
  });
  // Multi-Studio: Studios laden für Trainer-Multi-Select
  const [studios, setStudios] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get(`${API}/studios`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setStudios(Array.isArray(data) ? data : []);
      } catch {}
    })();
  }, []);

  useEffect(() => {
    fetchStaff();
  }, []);

  // Deep-Link von „Miet-Rechnungen": /staff?editUser=<id> öffnet direkt
  // den Bearbeiten-Dialog des Mieters (mit den Miet-Einstellungen).
  useEffect(() => {
    if (!staff || staff.length === 0) return;
    const editId = new URLSearchParams(window.location.search).get('editUser');
    if (!editId) return;
    const person = staff.find((u) => u.id === editId);
    if (person) {
      handleOpenEdit(person);
      window.history.replaceState({}, '', '/staff');
    }
  }, [staff]); // eslint-disable-line react-hooks/exhaustive-deps


  const fetchStaff = async () => {
    try {
      const token = localStorage.getItem('token');
      console.log('[StaffPage v1.0.2] Fetching staff, token exists:', !!token);
      const response = await axios.get(`${API}/users`, {
        params: { include_archived: true },
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('[StaffPage] Staff loaded:', response.data?.length, 'users');
      // Filter Admin, Trainer und Mieter
      const staffOnly = response.data.filter(u => u.role === 'admin' || u.role === 'trainer' || u.role === 'mieter');
      setStaff(staffOnly);
    } catch (error) {
      console.error('[StaffPage] Error loading staff:', error.response?.status, error.response?.data || error.message);
      toast.error('Fehler beim Laden des Personals: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data.filter(s => s.is_active));
      setServicesLoaded(true);
    } catch (error) {
      console.error('Error loading services:', error);
      toast.error('Fehler beim Laden der Services');
    }
  };

  const fetchRooms = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/resources`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filtere nur Räume (type === 'room')
      const roomsOnly = response.data.filter(r => r.type === 'room' && r.is_available);
      setRooms(roomsOnly);
      setRoomsLoaded(true);
    } catch (error) {
      console.error('Error loading rooms:', error);
    }
  };

  const ensureServicesLoaded = () => {
    if (!servicesLoaded) {
      fetchServices();
    }
  };

  const ensureRoomsLoaded = () => {
    if (!roomsLoaded) {
      fetchRooms();
    }
  };

  useEffect(() => {
    if (dialogOpen || editDialogOpen || availabilityDialogOpen) {
      ensureServicesLoaded();
    }
    if (dialogOpen || editDialogOpen) {
      ensureRoomsLoaded();
    }
  }, [dialogOpen, editDialogOpen, availabilityDialogOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        rent_amount: formData.rent_amount ? parseFloat(formData.rent_amount) : null,
      };
      await axios.post(`${API}/users/staff`, payload);
      toast.success('Personal erfolgreich hinzugefügt!');
      
      // Store credentials to show to admin
      setCreatedCredentials({
        name: `${formData.first_name} ${formData.last_name}`,
        email: formData.email,
        password: formData.password,
        role: formData.role
      });
      
      setDialogOpen(false);
      setCredentialsDialogOpen(true); // Show credentials dialog
      fetchStaff();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Hinzufügen');
    }
  };

  const handleOpenAvailability = async (trainer) => {
    setSelectedTrainer(trainer);
    try {
      const response = await axios.get(`${API}/trainers/${trainer.id}/availability`);
      const backendData = response.data;
      
      // Migrate old slot format (from/to) to new format (from_time/to_time) if needed
      const migratedData = { ...backendData };
      ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].forEach(day => {
        if (migratedData[day] && migratedData[day].slots) {
          migratedData[day].slots = migratedData[day].slots.map(slot => ({
            from_time: slot.from_time || slot.from || '09:00',
            to_time: slot.to_time || slot.to || '18:00',
            service_ids: slot.service_ids || [],
            is_customer_visible: slot.is_customer_visible !== undefined ? slot.is_customer_visible : true,
            // Multi-Studio: studio_id pro Slot persistieren
            studio_id: slot.studio_id || null,
            // Per-Slot Gültigkeitsdatum (06/2026): buchbar ab / bis
            valid_from: slot.valid_from || '',
            valid_until: slot.valid_until || '',
          }));
          
          // Ensure slot_duration exists
          if (!migratedData[day].slot_duration) {
            migratedData[day].slot_duration = '30min';
          }
        }
      });
      
      setAvailability(migratedData);
      setShowAvailHistory(false);
      try {
        const per = await axios.get(`${API}/trainers/${trainer.id}/availability-periods`);
        setAvailHistory(per.data?.history || []);
      } catch (_e) { setAvailHistory([]); }
    } catch (error) {
      console.error('Error loading trainer availability:', error);
    }
    setAvailabilityDialogOpen(true);
  };

  const handleSaveAvailability = async () => {
    if (!selectedTrainer) return;

    try {
      await axios.put(`${API}/trainers/${selectedTrainer.id}/availability`, availability);
      toast.success('Verfügbarkeit gespeichert!');
      try {
        const per = await axios.get(`${API}/trainers/${selectedTrainer.id}/availability-periods`);
        setAvailHistory(per.data?.history || []);
      } catch (_e) {}
      setAvailabilityDialogOpen(false);
    } catch (error) {
      // Backend liefert bei Studio-Konflikt einen detaillierten 400-Text
      // ("Studio-Konflikt am Dienstag: Slot 14:00–19:00 …"). Wir zeigen
      // diese Meldung wortwörtlich an, damit Admin/Trainer sofort weiß,
      // welcher Slot bei welchem Studio kollidiert.
      const detail = error?.response?.data?.detail;
      toast.error(
        typeof detail === 'string' && detail.length > 0
          ? detail
          : 'Fehler beim Speichern',
        { duration: 6000 },
      );
    }
  };

  // Mitarbeiter archivieren (statt hart löschen – Historie/Plantafel bleibt erhalten)
  const handleDeleteStaff = async (person) => {
    const confirmMessage = `${person.first_name} ${person.last_name} archivieren?\n\nDer Trainer bleibt in der Historie & Plantafel sichtbar (alte Termine, Verfügbarkeiten), erhält aber KEINE neuen Buchungen mehr. Kann jederzeit wieder aktiviert werden.`;
    
    if (!window.confirm(confirmMessage)) return;
    
    try {
      await axios.delete(`${API}/staff/${person.id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success(`${person.first_name} ${person.last_name} wurde archiviert`);
      fetchStaff();
    } catch (error) {
      console.error('Archive error:', error);
      const errorMsg = error.response?.data?.detail || 'Fehler beim Archivieren';
      toast.error(errorMsg);
    }
  };

  const handleRestoreStaff = async (person) => {
    if (!window.confirm(`${person.first_name} ${person.last_name} wieder aktivieren?\n\nDer Trainer erscheint danach wieder in Dropdowns/Buchungen.`)) return;
    try {
      await axios.post(`${API}/staff/${person.id}/restore`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success(`${person.first_name} ${person.last_name} wurde reaktiviert`);
      fetchStaff();
    } catch (error) {
      console.error('Restore error:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Reaktivieren');
    }
  };

  const handleOpenVacations = async (trainer) => {
    console.log('Opening vacations for:', trainer.first_name, trainer.last_name);
    setSelectedTrainer(trainer);
    
    // First load vacations
    try {
      const response = await axios.get(`${API}/trainers/${trainer.id}/vacations`);
      console.log('Vacations loaded:', response.data);
      setVacations(response.data || []);
    } catch (error) {
      console.error('Error loading vacations:', error);
      setVacations([]);
    }
    
    // Then open dialog (after state update)
    setTimeout(() => {
      setVacationDialogOpen(true);
      console.log('Dialog should be open now');
    }, 100);
  };

  const handleAddVacation = async () => {
    if (!vacationFormData.start_date || !vacationFormData.end_date) {
      toast.error('Bitte Start- und Enddatum eingeben');
      return;
    }

    try {
      const requestData = {
        trainer_id: selectedTrainer.id,
        start_date: vacationFormData.start_date,
        end_date: vacationFormData.end_date,
        notes: vacationFormData.notes
      };
      
      await axios.post(`${API}/trainers/${selectedTrainer.id}/vacations`, requestData);
      toast.success('Ferien hinzugefügt!');
      setVacationFormData({ start_date: '', end_date: '', notes: '' });
      // Reload vacations
      const response = await axios.get(`${API}/trainers/${selectedTrainer.id}/vacations`);
      setVacations(response.data || []);
    } catch (error) {
      console.error('Vacation add error:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Fehler beim Hinzufügen');
    }
  };

  const handleDeleteVacation = async (vacationId) => {
    if (!window.confirm('Ferien wirklich löschen?')) return;
    
    try {
      await axios.delete(`${API}/trainers/vacations/${vacationId}`);
      toast.success('Ferien gelöscht!');
      // Reload vacations
      const response = await axios.get(`${API}/trainers/${selectedTrainer.id}/vacations`);
      setVacations(response.data || []);
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };


  const handleOpenEdit = (person) => {
    setSelectedTrainer(person);
    setEditFormData({
      first_name: person.first_name,
      last_name: person.last_name,
      email: person.email,
      phone: person.phone || '',
      role: person.role,
      new_password: '', // Leeres Passwort-Feld beim Öffnen
      // Mieter-spezifische Felder
      rent_amount: person.rent_amount || '',
      rent_payment_interval: person.rent_payment_interval || 'monthly',
      rented_room: person.rented_room || '',
      rent_start_date: person.rent_start_date || '',
      rent_first_billing_month: person.rent_first_billing_month || '',
      rent_auto_invoice: person.rent_auto_invoice !== false,  // default true
      street: person.street || '',
      postal_code: person.postal_code || '',
      city: person.city || '',
      studio_ids: person.studio_ids || [],
      exclude_from_auto_assignment: person.exclude_from_auto_assignment === true,
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!selectedTrainer) return;
    
    try {
      // Prepare data - convert types if needed
      const updateData = {
        ...editFormData,
        rent_amount: editFormData.rent_amount ? parseFloat(editFormData.rent_amount) : null
      };
      
      // Remove any fields that shouldn't be sent
      delete updateData.rent_due_day;  // Deprecated field
      
      // Passwort nur senden wenn ausgefüllt
      if (!updateData.new_password) {
        delete updateData.new_password;
      }
      
      // Update user via PUT endpoint
      await axios.put(`${API}/users/${selectedTrainer.id}`, updateData);
      toast.success('Personal aktualisiert!');
      setEditDialogOpen(false);
      fetchStaff();
    } catch (error) {
      console.error('Update error:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Fehler beim Aktualisieren');
    }
  };

  // ============= PERMISSIONS FUNCTIONS =============
  const handleOpenPermissions = async (trainer) => {
    setSelectedTrainer(trainer);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users/${trainer.id}/permissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedPermissions(response.data.permissions || DEFAULT_PERMISSIONS);
      setPermissionsDialogOpen(true);
    } catch (error) {
      console.error('Error loading permissions:', error);
      // Use defaults if not found
      setSelectedPermissions(DEFAULT_PERMISSIONS);
      setPermissionsDialogOpen(true);
    }
  };

  const handlePermissionChange = (key, value) => {
    setSelectedPermissions(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSavePermissions = async () => {
    if (!selectedTrainer) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/users/${selectedTrainer.id}/permissions`,
        selectedPermissions,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Berechtigungen gespeichert!');
      setPermissionsDialogOpen(false);
      fetchStaff();
    } catch (error) {
      console.error('Error saving permissions:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const toggleDayAvailability = (dayKey) => {
    setAvailability({
      ...availability,
      [dayKey]: {
        ...availability[dayKey],
        active: !availability[dayKey].active
      }
    });
  };

  const updateDaySlot = (dayKey, slotIndex, field, value) => {
    const newSlots = [...availability[dayKey].slots];
    newSlots[slotIndex][field] = value;
    setAvailability({
      ...availability,
      [dayKey]: {
        ...availability[dayKey],
        slots: newSlots
      }
    });
  };

  const addSlot = (dayKey) => {
    setAvailability({
      ...availability,
      [dayKey]: {
        ...availability[dayKey],
        slots: [...availability[dayKey].slots, { from_time: '09:00', to_time: '18:00', service_ids: [], is_customer_visible: true, studio_id: null, valid_from: '', valid_until: '' }]
      }
    });
  };

  const toggleServiceInSlot = (dayKey, slotIndex, serviceId) => {
    const newSlots = [...availability[dayKey].slots];
    const currentServiceIds = newSlots[slotIndex].service_ids || [];
    
    if (currentServiceIds.includes(serviceId)) {
      // Remove service
      newSlots[slotIndex].service_ids = currentServiceIds.filter(id => id !== serviceId);
    } else {
      // Add service
      newSlots[slotIndex].service_ids = [...currentServiceIds, serviceId];
    }
    
    setAvailability({
      ...availability,
      [dayKey]: {
        ...availability[dayKey],
        slots: newSlots
      }
    });
  };

  const toggleSlotVisibility = (dayKey, slotIndex) => {
    const newSlots = [...availability[dayKey].slots];
    newSlots[slotIndex].is_customer_visible = !newSlots[slotIndex].is_customer_visible;
    setAvailability({
      ...availability,
      [dayKey]: {
        ...availability[dayKey],
        slots: newSlots
      }
    });
  };

  const removeSlot = (dayKey, slotIndex) => {
    const newSlots = availability[dayKey].slots.filter((_, i) => i !== slotIndex);
    setAvailability({
      ...availability,
      [dayKey]: {
        ...availability[dayKey],
        slots: newSlots
      }
    });
  };

  const getDayKey = (day) => {
    const dayMap = {
      'Montag': 'monday',
      'Dienstag': 'tuesday',
      'Mittwoch': 'wednesday',
      'Donnerstag': 'thursday',
      'Freitag': 'friday',
      'Samstag': 'saturday',
      'Sonntag': 'sunday'
    };
    return dayMap[day];
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      role: 'trainer',
      phone: '',
      studio_ids: [],
      rent_amount: '',
      rent_payment_interval: 'monthly',
      rented_room: '',
      rent_start_date: '',
      rent_first_billing_month: '',
      rent_auto_invoice: true,
      street: '',
      postal_code: '',
      city: '',
    });
  };

  const getRoleBadge = (role) => {
    const colors = {
      admin: 'bg-red-100 text-red-800',
      trainer: 'bg-blue-100 text-blue-800',
      mieter: 'bg-green-100 text-green-800'
    };
    
    const labels = {
      admin: 'Admin',
      trainer: 'Trainer',
      mieter: 'Mieter'
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[role] || 'bg-gray-100 text-gray-800'}`}>
        {labels[role] || role}
      </span>
    );
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div></div>;
  }

  // Version for deployment tracking
  const STAFF_VERSION = "v1.0.2";

  // Archiviert = soft-deleted Trainer (is_archived oder is_active=false)
  const isArchived = (p) => p.is_archived === true || p.is_active === false;
  const archivedCount = staff.filter(isArchived).length;
  const displayedStaff = showArchived ? staff : staff.filter((p) => !isArchived(p));

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="staff-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        {!embedded && (
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <UserCircle size={28} className="sm:w-8 sm:h-8" />
            Personal
            <span className="text-xs text-gray-400 font-normal ml-2">{STAFF_VERSION}</span>
          </h1>
          <p className="text-gray-600 mt-1 text-sm sm:text-base">Verwalten Sie Trainer, Mieter und Administratoren</p>
        </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          {!embedded && (
          <>
          <Button asChild variant="outline" data-testid="staff-timetracking-btn">
            <Link to="/timetracking">
              <Clock size={16} className="mr-2" />
              Stundenerfassung
            </Link>
          </Button>
          <Button asChild variant="outline" data-testid="staff-payroll-btn">
            <Link to="/timetracking?tab=payroll">
              <CreditCard size={16} className="mr-2" />
              Lohn & AHV
            </Link>
          </Button>
          </>
          )}
          <div className="flex items-center gap-2 rounded-md border px-3 py-1.5" data-testid="show-archived-toggle-wrapper">
            <Switch
              id="show-archived-switch"
              checked={showArchived}
              onCheckedChange={setShowArchived}
              data-testid="show-archived-switch"
            />
            <Label htmlFor="show-archived-switch" className="text-sm cursor-pointer whitespace-nowrap">
              Archivierte anzeigen{archivedCount > 0 ? ` (${archivedCount})` : ''}
            </Label>
          </div>
          <EmployeeOnboardingInviteButton />
          <TrainerOrderManager />
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-staff-button">
              <Plus size={16} className="mr-2" />
              Neues Personal
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Neues Personal hinzufügen</DialogTitle>
              <DialogDescription>Erstellen Sie einen neuen Mitarbeiter-Account</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">Vorname</Label>
                  <Input
                    id="first_name"
                    data-testid="staff-firstname-input"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Nachname</Label>
                  <Input
                    id="last_name"
                    data-testid="staff-lastname-input"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">E-Mail</Label>
                <Input
                  id="email"
                  data-testid="staff-email-input"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Telefon (optional)</Label>
                <PhoneInput
                  id="phone"
                  data-testid="staff-phone-input"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="role">Rolle</Label>
                <Select 
                  value={formData.role} 
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger data-testid="staff-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="trainer">Trainer (Termine verwalten)</SelectItem>
                    <SelectItem value="mieter">Mieter (nur Ressourcen)</SelectItem>
                    <SelectItem value="admin">Admin (Vollzugriff)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">
                  {formData.role === 'trainer' && '→ Kann Termine und Ressourcen sehen'}
                  {formData.role === 'mieter' && '→ Kann nur Ressourcen/Räume buchen'}
                  {formData.role === 'admin' && '→ Vollständiger Zugriff auf alles'}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Passwort</Label>
                <Input
                  id="password"
                  data-testid="staff-password-input"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                />
              </div>

              {/* Multi-Studio-Zuordnung */}
              {studios.length > 1 && (
                <div className="space-y-2 p-3 bg-slate-50 rounded-lg">
                  <Label className="flex items-center gap-1">📍 In welchen Studios arbeitet diese Person?</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {studios.map((s) => {
                      const isSelected = (formData.studio_ids || []).includes(s.id);
                      return (
                        <button
                          key={s.id}
                          type="button"
                          data-testid={`staff-studio-chip-${s.short_name || s.id}`}
                          onClick={() => {
                            const next = isSelected
                              ? formData.studio_ids.filter((id) => id !== s.id)
                              : [...(formData.studio_ids || []), s.id];
                            setFormData({ ...formData, studio_ids: next });
                          }}
                          className={`px-3 py-1.5 rounded-full text-sm font-medium transition border-2 ${
                            isSelected
                              ? 'bg-amber-500 text-white border-amber-600 shadow'
                              : 'bg-white text-gray-700 border-gray-200 hover:border-amber-400'
                          }`}
                        >
                          {isSelected && '✓ '}📍 {s.short_name || s.name}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Mehrfach-Auswahl möglich. Wirkt sich auf Kalender-Studio-Filter aus.
                  </p>
                </div>
              )}
              {/* Mieter-spezifische Felder beim Anlegen */}
              {formData.role === 'mieter' && (
                <div className="border-t pt-4 mt-4 space-y-4">
                  <h4 className="font-semibold text-sm text-gray-700 flex items-center gap-2">
                    🏠 Miet-Einstellungen
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="new_rent_amount">Mietbetrag (CHF)</Label>
                      <Input
                        id="new_rent_amount"
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.rent_amount}
                        onChange={(e) => setFormData({ ...formData, rent_amount: e.target.value })}
                        placeholder="z.B. 500.00"
                        data-testid="new-rent-amount-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="new_rent_payment_interval">Zahlungsintervall</Label>
                      <Select
                        value={formData.rent_payment_interval}
                        onValueChange={(value) => setFormData({ ...formData, rent_payment_interval: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="monthly">Monatlich</SelectItem>
                          <SelectItem value="bimonthly">Alle 2 Monate</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="new_rented_room">Gemieteter Raum</Label>
                      <Select
                        value={formData.rented_room}
                        onValueChange={(value) => setFormData({ ...formData, rented_room: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Raum wählen" />
                        </SelectTrigger>
                        <SelectContent>
                          {rooms.length === 0 ? (
                            <div className="px-2 py-1.5 text-sm text-gray-400">Keine Räume in Ressourcen</div>
                          ) : (
                            rooms.map(room => (
                              <SelectItem key={room.id} value={room.name}>{room.name}</SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="new_rent_start_date">Mietbeginn</Label>
                      <Input
                        id="new_rent_start_date"
                        type="date"
                        value={formData.rent_start_date}
                        onChange={(e) => setFormData({ ...formData, rent_start_date: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Adresse (für QR-Rechnung „Zahlbar durch")</Label>
                    <Input
                      placeholder="Strasse und Nr."
                      value={formData.street}
                      onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                      data-testid="new-rent-street-input"
                    />
                    <div className="grid grid-cols-3 gap-2">
                      <Input
                        placeholder="PLZ"
                        value={formData.postal_code}
                        onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                        data-testid="new-rent-postal-input"
                      />
                      <Input
                        className="col-span-2"
                        placeholder="Ort"
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        data-testid="new-rent-city-input"
                      />
                    </div>
                    <p className="text-xs text-gray-500">Ohne PLZ + Ort bleibt „Zahlbar durch" auf der Rechnung leer.</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new_rent_first_billing_month">Erste Rechnung für Monat</Label>
                    <Input
                      id="new_rent_first_billing_month"
                      type="month"
                      value={formData.rent_first_billing_month}
                      onChange={(e) => setFormData({ ...formData, rent_first_billing_month: e.target.value })}
                      placeholder="z.B. 2026-02"
                    />
                    <p className="text-xs text-gray-500">
                      Monat, für den die erste Mietrechnung erstellt werden soll. Leer lassen = ab aktuellem Monat.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Rechnungserstellung</Label>
                    <Select
                      value={formData.rent_auto_invoice ? 'auto' : 'manual'}
                      onValueChange={(value) => setFormData({ ...formData, rent_auto_invoice: value === 'auto' })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto">Automatisch (fester Betrag)</SelectItem>
                        <SelectItem value="manual">Manuell freigeben (variabler Betrag)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500">
                      {formData.rent_auto_invoice
                        ? 'Rechnung wird automatisch am 1. des Monats erstellt und versendet'
                        : 'Rechnung wird 5 Tage vorher als Entwurf erstellt - Betrag anpassen und freigeben'}
                    </p>
                  </div>
                </div>
              )}
              <Button type="submit" className="w-full" data-testid="submit-staff-button">
                Personal hinzufügen
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="sm:hidden space-y-3">
        {displayedStaff.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-gray-500">
              Kein Personal vorhanden
            </CardContent>
          </Card>
        ) : (
          displayedStaff.map((person, index) => (
            <Card key={person.id} data-testid={`staff-card-${index}`} className={isArchived(person) ? 'opacity-70' : ''}>
              <CardContent className="p-4">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="font-medium">{person.first_name} {person.last_name}</p>
                    <p className="text-sm text-gray-600">{person.email}</p>
                    {person.phone && <p className="text-sm text-gray-500">{person.phone}</p>}
                  </div>
                  <div className="flex gap-2 items-center">
                    {getRoleBadge(person.role)}
                    {isArchived(person) && (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-700">
                        Archiviert
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex justify-end">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" className="px-2" data-testid={`staff-actions-card-${person.id}`}>
                        <MoreVertical size={16} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-52">
                      <DropdownMenuItem onClick={() => handleOpenEdit(person)}>
                        <Edit size={14} className="mr-2" /> Bearbeiten
                      </DropdownMenuItem>
                      {(person.role === 'trainer' || person.role === 'admin') && (
                        <>
                          <DropdownMenuItem onClick={() => handleOpenAvailability(person)}>
                            <Calendar size={14} className="mr-2" /> Verfügbarkeit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleOpenVacations(person)}>
                            🏖️ <span className="ml-2">Ferien</span>
                          </DropdownMenuItem>
                        </>
                      )}
                      {person.role === 'trainer' && (
                        <DropdownMenuItem onClick={() => handleOpenPermissions(person)} className="text-purple-700">
                          <Shield size={14} className="mr-2" /> Rechte
                        </DropdownMenuItem>
                      )}
                      {person.role === 'trainer' && (
                        <DropdownMenuItem onClick={() => navigate(`/trainers/${person.id}`)} className="text-emerald-700" data-testid={`profitability-card-btn-${person.id}`}>
                          <TrendingUp size={14} className="mr-2" /> Rentabilität
                        </DropdownMenuItem>
                      )}
                      {(person.role === 'trainer' || person.role === 'admin') && (
                        <>
                          <EmployeeMasterDataButton asMenuItem userId={person.id} name={`${person.first_name} ${person.last_name}`} />
                          <EmployeeInviteResendButton asMenuItem userId={person.id} />
                        </>
                      )}
                      <DropdownMenuSeparator />
                      {isArchived(person) ? (
                        <DropdownMenuItem onClick={() => handleRestoreStaff(person)} className="text-green-700">
                          <RotateCcw size={14} className="mr-2" /> Reaktivieren
                        </DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem onClick={() => handleDeleteStaff(person)} className="text-red-600">
                          <Trash2 size={14} className="mr-2" /> Archivieren
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Desktop Table View */}
      <Card className="hidden sm:block">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead className="hidden md:table-cell">E-Mail</TableHead>
                <TableHead className="hidden lg:table-cell">Telefon</TableHead>
                <TableHead>Rolle</TableHead>
                <TableHead>{/* 06/2026 — Status entfernt */}</TableHead>
                <TableHead>Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayedStaff.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                    Kein Personal vorhanden
                  </TableCell>
                </TableRow>
              ) : (
                displayedStaff.map((person, index) => (
                  <TableRow key={person.id} data-testid={`staff-row-${index}`} className={isArchived(person) ? 'opacity-70' : ''}>
                    <TableCell className="font-medium">
                      {person.first_name} {person.last_name}
                      <span className="md:hidden block text-xs text-gray-500">{person.email}</span>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">{person.email}</TableCell>
                    <TableCell className="hidden lg:table-cell">{person.phone || '-'}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getRoleBadge(person.role)}
                        {isArchived(person) && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-700" data-testid={`archived-badge-${person.id}`}>
                            Archiviert
                          </span>
                        )}
                      </div>
                    </TableCell>
                    {/* 06/2026 — Status-Spalte entfernt (siehe Cards-View). */}
                    <TableCell>
                      <div className="flex justify-end">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="px-2"
                              data-testid={`staff-actions-${person.id}`}
                            >
                              <MoreVertical size={16} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-52">
                            <DropdownMenuItem onClick={() => handleOpenEdit(person)}>
                              <Edit size={14} className="mr-2" /> Bearbeiten
                            </DropdownMenuItem>
                            {/* Nur Trainer und Admin haben Verfügbarkeit und Ferien */}
                            {(person.role === 'trainer' || person.role === 'admin') && (
                              <>
                                <DropdownMenuItem onClick={() => handleOpenAvailability(person)}>
                                  <Calendar size={14} className="mr-2" /> Verfügbarkeit
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleOpenVacations(person)}>
                                  🏖️ <span className="ml-2">Ferien</span>
                                </DropdownMenuItem>
                              </>
                            )}
                            {/* Nur Trainer haben konfigurierbare Berechtigungen */}
                            {person.role === 'trainer' && (
                              <DropdownMenuItem
                                onClick={() => handleOpenPermissions(person)}
                                className="text-purple-700"
                                data-testid={`permissions-btn-${person.id}`}
                              >
                                <Shield size={14} className="mr-2" /> Rechte
                              </DropdownMenuItem>
                            )}
                            {person.role === 'trainer' && (
                              <DropdownMenuItem
                                onClick={() => navigate(`/trainers/${person.id}`)}
                                className="text-emerald-700"
                                data-testid={`profitability-btn-${person.id}`}
                              >
                                <TrendingUp size={14} className="mr-2" /> Rentabilität
                              </DropdownMenuItem>
                            )}
                            {/* Stammdaten (AHV/Adresse/Bank) bearbeiten → ans ERP */}
                            {(person.role === 'trainer' || person.role === 'admin') && (
                              <>
                                <EmployeeMasterDataButton asMenuItem userId={person.id} name={`${person.first_name} ${person.last_name}`} />
                                <EmployeeInviteResendButton asMenuItem userId={person.id} />
                              </>
                            )}
                            <DropdownMenuSeparator />
                            {/* Archivieren / Reaktivieren */}
                            {isArchived(person) ? (
                              <DropdownMenuItem
                                onClick={() => handleRestoreStaff(person)}
                                className="text-green-700"
                                data-testid={`restore-btn-${person.id}`}
                              >
                                <RotateCcw size={14} className="mr-2" /> Reaktivieren
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem
                                onClick={() => handleDeleteStaff(person)}
                                className="text-red-600"
                                data-testid={`delete-btn-${person.id}`}
                              >
                                <Trash2 size={14} className="mr-2" /> Archivieren
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Availability Configuration Dialog */}
      <Dialog open={availabilityDialogOpen} onOpenChange={setAvailabilityDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar size={24} />
              Verfügbarkeit konfigurieren - {selectedTrainer?.first_name} {selectedTrainer?.last_name}
            </DialogTitle>
            <DialogDescription>Arbeitszeiten und verfügbare Services festlegen</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="mb-4">
              <Label>Trainer-Farbe (für Kalenderansicht)</Label>
              <Input
                type="color"
                value={availability.color || '#3B82F6'}
                onChange={(e) => setAvailability({...availability, color: e.target.value})}
                className="w-24 h-10"
              />
            </div>

            <div className="border-t pt-4">
              <Label className="font-semibold">Verfügbarkeit & Datum</Label>
              <p className="text-xs text-gray-500 mb-2">
                Die Verfügbarkeit ist <b>ab sofort aktiv</b> – Kunden können sofort buchen. Pro Zeitslot kannst du unten optional ein <b>„Buchbar ab"</b> und <b>„Buchbar bis"</b> Datum setzen: Kunden bekommen dann für diesen Slot erst ab bzw. nur bis zu diesem Datum Termine angeboten. Leer = keine Datumsgrenze. Bereits gebuchte Termine – auch Serien – bleiben immer unberührt.
              </p>
              {availHistory.length > 0 && (
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={() => setShowAvailHistory((v) => !v)}
                    className="flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-gray-900"
                    data-testid="avail-history-toggle"
                  >
                    {showAvailHistory ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    Verlauf früherer Verfügbarkeiten ({availHistory.length})
                  </button>
                  {showAvailHistory && (
                    <div className="mt-2 space-y-2">
                      <p className="text-[11px] text-gray-400">
                        Archivierte Versionen (nicht löschbar). Jede Karte ist eine früher gespeicherte Wochenplanung – neueste zuerst.
                      </p>
                      <div className="max-h-56 overflow-y-auto space-y-2 pr-1">
                        {availHistory.map((h, i) => (
                          <div key={i} className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-xs" data-testid={`avail-history-${i}`}>
                            <div className="font-semibold text-gray-700 mb-0.5">
                              {h.archived_at ? new Date(h.archived_at).toLocaleString('de-CH', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}
                            </div>
                            <div className="text-gray-600 leading-relaxed">{h.summary}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>


            <div className="border-t pt-4">
              <h3 className="font-semibold mb-3">Service-spezifische Verfügbarkeit</h3>
              <p className="text-sm text-gray-600 mb-4">
                Konfigurieren Sie für jeden Wochentag individuelle Zeitslots mit spezifischen Services und Sichtbarkeitseinstellungen.
                Leere Service-Auswahl bedeutet: Alle Services sind erlaubt.
              </p>

            {['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'].map((day) => {
              const dayKey = getDayKey(day);
              const dayData = availability[dayKey];
              
              return (
                <div key={day} className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Switch
                        checked={dayData?.active || false}
                        onCheckedChange={() => toggleDayAvailability(dayKey)}
                      />
                      <Label className="text-lg font-semibold">{day}</Label>
                    </div>
                    <div className="flex items-center gap-4">
                      {dayData?.active && (
                        <Select 
                          value={dayData?.slot_duration || '30min'}
                          onValueChange={(value) => {
                            setAvailability({
                              ...availability,
                              [dayKey]: {
                                ...availability[dayKey],
                                slot_duration: value
                              }
                            });
                          }}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="15min">15-Min-Slots</SelectItem>
                            <SelectItem value="30min">30-Min-Slots</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                      <span className="text-sm text-gray-500">
                        {dayData?.active ? 'Verfügbar' : 'Nicht verfügbar'}
                      </span>
                    </div>
                  </div>
                  
                  {dayData?.active && (
                    <div className="ml-9 space-y-4">
                      {dayData.slots?.map((slot, index) => (
                        <div key={index} className="border-l-4 border-blue-400 pl-4 space-y-3 bg-gray-50 p-3 rounded">
                          <div className="flex items-start gap-3">
                            <div className="flex-1 space-y-3">
                              {/* Time Range */}
                              <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1">
                                  <Label className="text-xs font-semibold">Von</Label>
                                  <Input
                                    type="time"
                                    value={slot.from_time || slot.from || '09:00'}
                                    onChange={(e) => updateDaySlot(dayKey, index, 'from_time', e.target.value)}
                                  />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs font-semibold">Bis</Label>
                                  <Input
                                    type="time"
                                    value={slot.to_time || slot.to || '18:00'}
                                    onChange={(e) => updateDaySlot(dayKey, index, 'to_time', e.target.value)}
                                  />
                                </div>
                              </div>

                              {/* Per-Slot Gültigkeitsdatum (06/2026) */}
                              <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1">
                                  <Label className="text-xs font-semibold">📅 Buchbar ab</Label>
                                  <Input
                                    type="date"
                                    value={slot.valid_from || ''}
                                    onChange={(e) => updateDaySlot(dayKey, index, 'valid_from', e.target.value)}
                                    data-testid={`slot-valid-from-${dayKey}-${index}`}
                                  />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs font-semibold">Buchbar bis (leer = unbegrenzt)</Label>
                                  <Input
                                    type="date"
                                    value={slot.valid_until || ''}
                                    onChange={(e) => updateDaySlot(dayKey, index, 'valid_until', e.target.value)}
                                    data-testid={`slot-valid-until-${dayKey}-${index}`}
                                  />
                                </div>
                              </div>
                              {(slot.valid_from || slot.valid_until) && (
                                <p className="text-[10px] text-blue-600 italic">
                                  Kunden buchen diesen Slot{slot.valid_from ? ` ab ${slot.valid_from}` : ''}{slot.valid_until ? ` bis ${slot.valid_until}` : ''}. Admin kann jederzeit manuell eintragen.
                                </p>
                              )}
                              {(() => {
                                const today = new Date().toISOString().slice(0, 10);
                                const adminOnly = !(slot.is_customer_visible !== undefined ? slot.is_customer_visible : true);
                                const expired = slot.valid_until && slot.valid_until < today;
                                const notYet = slot.valid_from && slot.valid_from > today;
                                if (!adminOnly && !expired && !notYet) return null;
                                let label = '🔒 Aktuell nur Admin/Trainer buchbar';
                                if (expired) label = '⏳ Abgelaufen – nur Admin/Trainer buchbar (neues Datum setzen)';
                                else if (notYet) label = `🗓️ Bis ${slot.valid_from} nur Admin/Trainer buchbar`;
                                else if (adminOnly) label = '🔒 Nur Admin/Trainer buchbar (für Kunden ausgeblendet)';
                                return (
                                  <div
                                    className="flex items-center gap-1.5 text-xs font-medium text-amber-800 bg-amber-100 border border-amber-300 rounded px-2 py-1.5"
                                    data-testid={`slot-adminonly-badge-${dayKey}-${index}`}
                                  >
                                    {label}
                                  </div>
                                );
                              })()}

                              {/* Multi-Studio: Studio-Auswahl pro Slot */}
                              {studios.length > 1 && (
                                <div className="space-y-1">
                                  <Label className="text-xs font-semibold flex items-center gap-1">
                                    🏢 Studio
                                  </Label>
                                  <select
                                    value={slot.studio_id || ''}
                                    onChange={(e) => updateDaySlot(dayKey, index, 'studio_id', e.target.value || null)}
                                    className="w-full px-3 py-2 border rounded-md text-sm bg-white"
                                    data-testid={`slot-studio-${dayKey}-${index}`}
                                  >
                                    <option value="">— Alle Studios (kein Filter) —</option>
                                    {studios.map(s => (
                                      <option key={s.id} value={s.id}>
                                        {s.name || s.short_name}
                                      </option>
                                    ))}
                                  </select>
                                  {!slot.studio_id && (
                                    <p className="text-[10px] text-gray-500 italic">
                                      Slot ist in jedem Studio buchbar
                                    </p>
                                  )}
                                </div>
                              )}
                              
                              {/* Service Selection */}
                              <div className="space-y-2">
                                <Label className="text-xs font-semibold">Verfügbare Services</Label>
                                <div className="text-xs text-gray-600 mb-2">
                                  {slot.service_ids && slot.service_ids.length > 0 
                                    ? `${slot.service_ids.length} Service(s) ausgewählt` 
                                    : 'Alle Services verfügbar (keine Einschränkung)'}
                                </div>
                                <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto p-2 border rounded bg-white">
                                  {services.map(service => (
                                    <label key={service.id} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-gray-50 p-1 rounded">
                                      <input
                                        type="checkbox"
                                        checked={(slot.service_ids || []).includes(service.id)}
                                        onChange={() => toggleServiceInSlot(dayKey, index, service.id)}
                                        className="rounded"
                                      />
                                      <span className="truncate">{service.name}</span>
                                    </label>
                                  ))}
                                </div>
                              </div>
                              
                              {/* Customer Visibility */}
                              <div className="flex items-center justify-between p-2 bg-white rounded border">
                                <div className="flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={!(slot.is_customer_visible !== undefined ? slot.is_customer_visible : true)}
                                    onChange={() => toggleSlotVisibility(dayKey, index)}
                                    className="rounded"
                                  />
                                  <Label className="text-xs font-medium cursor-pointer">
                                    🔒 Nur für Admin/Trainer sichtbar
                                  </Label>
                                </div>
                                {!(slot.is_customer_visible !== undefined ? slot.is_customer_visible : true) && (
                                  <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">
                                    Kunden sehen diesen Slot nicht
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            {/* Delete Button */}
                            {dayData.slots.length > 1 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeSlot(dayKey, index)}
                                className="text-red-600 hover:text-red-700 mt-1"
                              >
                                🗑️
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => addSlot(dayKey)}
                        className="text-xs w-full"
                      >
                        + Weiteren Zeitslot hinzufügen
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
            </div>
            
            {/* Ad-hoc Verfügbarkeit */}
            <div className="border-t pt-4 mt-4">
              <h3 className="font-semibold mb-2">Spezielle Verfügbarkeit (bestimmte Tage)</h3>
              <p className="text-sm text-gray-600 mb-4">
                Hier können Sie den Trainer für bestimmte Tage manuell verfügbar oder nicht verfügbar machen (überschreibt wöchentliche Einstellungen)
              </p>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  const dateStr = new Date().toISOString().split('T')[0];
                  setAvailability({
                    ...availability,
                    special_dates: {
                      ...availability.special_dates,
                      [dateStr]: { active: true, slots: [{ from: '09:00', to: '18:00' }] }
                    }
                  });
                }}
              >
                + Spezielles Datum hinzufügen
              </Button>
              
              {availability.special_dates && Object.keys(availability.special_dates).length > 0 && (
                <div className="mt-4 space-y-3">
                  {Object.entries(availability.special_dates).map(([dateStr, dayData]) => (
                    <div key={dateStr} className="border rounded-lg p-3 bg-amber-50">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <Switch
                            checked={dayData.active}
                            onCheckedChange={() => {
                              setAvailability({
                                ...availability,
                                special_dates: {
                                  ...availability.special_dates,
                                  [dateStr]: { ...dayData, active: !dayData.active }
                                }
                              });
                            }}
                          />
                          <Label className="font-semibold">
                            {new Date(dateStr).toLocaleDateString('de-DE', { 
                              weekday: 'long', 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            })}
                          </Label>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const newSpecialDates = {...availability.special_dates};
                            delete newSpecialDates[dateStr];
                            setAvailability({...availability, special_dates: newSpecialDates});
                          }}
                          className="text-red-600 hover:text-red-700"
                        >
                          ✕ Entfernen
                        </Button>
                      </div>
                      
                      {dayData.active && (
                        <div className="ml-9 space-y-2">
                          {dayData.slots?.map((slot, index) => (
                            <div key={index} className="flex gap-3 items-center">
                              <div className="flex-1 grid grid-cols-2 gap-3">
                                <Input
                                  type="time"
                                  value={slot.from}
                                  onChange={(e) => {
                                    const newSlots = [...dayData.slots];
                                    newSlots[index].from = e.target.value;
                                    setAvailability({
                                      ...availability,
                                      special_dates: {
                                        ...availability.special_dates,
                                        [dateStr]: { ...dayData, slots: newSlots }
                                      }
                                    });
                                  }}
                                />
                                <Input
                                  type="time"
                                  value={slot.to}
                                  onChange={(e) => {
                                    const newSlots = [...dayData.slots];
                                    newSlots[index].to = e.target.value;
                                    setAvailability({
                                      ...availability,
                                      special_dates: {
                                        ...availability.special_dates,
                                        [dateStr]: { ...dayData, slots: newSlots }
                                      }
                                    });
                                  }}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setAvailabilityDialogOpen(false)}>
                Abbrechen
              </Button>
              <Button onClick={handleSaveAvailability} className="flex items-center gap-2">
                <Save size={16} />
                Verfügbarkeit speichern
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Staff Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Personal bearbeiten</DialogTitle>
            <DialogDescription>Mitarbeiter-Daten aktualisieren</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_first_name">Vorname</Label>
                <Input
                  id="edit_first_name"
                  value={editFormData.first_name}
                  onChange={(e) => setEditFormData({ ...editFormData, first_name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_last_name">Nachname</Label>
                <Input
                  id="edit_last_name"
                  value={editFormData.last_name}
                  onChange={(e) => setEditFormData({ ...editFormData, last_name: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit_email">E-Mail</Label>
              <Input
                id="edit_email"
                type="email"
                value={editFormData.email}
                onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit_phone">Telefon (optional)</Label>
              <PhoneInput
                id="edit_phone"
                value={editFormData.phone}
                onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
              />
            </div>
            
            {/* Passwort ändern */}
            <div className="space-y-2">
              <Label htmlFor="edit_password">Neues Passwort (optional)</Label>
              <Input
                id="edit_password"
                type="password"
                value={editFormData.new_password}
                onChange={(e) => setEditFormData({ ...editFormData, new_password: e.target.value })}
                placeholder="Leer lassen = keine Änderung"
                data-testid="edit-password-input"
              />
              <p className="text-xs text-gray-500">
                Nur ausfüllen, wenn das Passwort geändert werden soll
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit_role">Rolle</Label>
              <Select 
                value={editFormData.role} 
                onValueChange={(value) => setEditFormData({ ...editFormData, role: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="trainer">Trainer (Termine verwalten)</SelectItem>
                  <SelectItem value="mieter">Mieter (nur Ressourcen)</SelectItem>
                  <SelectItem value="admin">Admin (Vollzugriff)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Multi-Studio Edit */}
            {studios.length > 1 && (
              <div className="space-y-2 p-3 bg-slate-50 rounded-lg">
                <Label className="flex items-center gap-1">📍 Studio-Zuordnung</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {studios.map((s) => {
                    const isSelected = (editFormData.studio_ids || []).includes(s.id);
                    return (
                      <button
                        key={s.id}
                        type="button"
                        data-testid={`staff-edit-studio-chip-${s.short_name || s.id}`}
                        onClick={() => {
                          const next = isSelected
                            ? editFormData.studio_ids.filter((id) => id !== s.id)
                            : [...(editFormData.studio_ids || []), s.id];
                          setEditFormData({ ...editFormData, studio_ids: next });
                        }}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition border-2 ${
                          isSelected
                            ? 'bg-amber-500 text-white border-amber-600 shadow'
                            : 'bg-white text-gray-700 border-gray-200 hover:border-amber-400'
                        }`}
                      >
                        {isSelected && '✓ '}📍 {s.short_name || s.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Von automatischer Trainer-Zuweisung ausschließen (Sammelkonten) */}
            {(editFormData.role === 'trainer' || editFormData.role === 'admin') && (
              <div className="flex items-start justify-between gap-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="space-y-0.5">
                  <Label htmlFor="exclude_auto_assign" className="text-sm font-medium">
                    Von automatischer Zuweisung ausschließen
                  </Label>
                  <p className="text-xs text-gray-600">
                    Für System-/Sammelkonten (z.B. „StrongFit Team"). Termine ohne Trainer
                    werden diesem Konto nicht automatisch zugewiesen – es wird immer ein echter Trainer gewählt.
                  </p>
                </div>
                <Switch
                  id="exclude_auto_assign"
                  data-testid="staff-edit-exclude-auto-assign"
                  checked={editFormData.exclude_from_auto_assignment === true}
                  onCheckedChange={(v) => setEditFormData({ ...editFormData, exclude_from_auto_assignment: v })}
                />
              </div>
            )}

            {/* Mieter-spezifische Felder */}
            {editFormData.role === 'mieter' && (
              <div className="border-t pt-4 mt-4 space-y-4">
                <h4 className="font-semibold text-sm text-gray-700 flex items-center gap-2">
                  🏠 Miet-Einstellungen
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="rent_amount">Mietbetrag (CHF)</Label>
                    <Input
                      id="rent_amount"
                      type="number"
                      step="0.01"
                      min="0"
                      value={editFormData.rent_amount}
                      onChange={(e) => setEditFormData({ ...editFormData, rent_amount: e.target.value })}
                      placeholder="z.B. 500.00"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="rent_payment_interval">Zahlungsintervall</Label>
                    <Select 
                      value={editFormData.rent_payment_interval} 
                      onValueChange={(value) => setEditFormData({ ...editFormData, rent_payment_interval: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Monatlich</SelectItem>
                        <SelectItem value="bimonthly">Alle 2 Monate</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="rented_room">Gemieteter Raum</Label>
                    <Select 
                      value={editFormData.rented_room} 
                      onValueChange={(value) => setEditFormData({ ...editFormData, rented_room: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Raum wählen" />
                      </SelectTrigger>
                      <SelectContent>
                        {rooms.length === 0 ? (
                          <div className="px-2 py-1.5 text-sm text-gray-400">Keine Räume in Ressourcen</div>
                        ) : (
                          rooms.map(room => (
                            <SelectItem key={room.id} value={room.name}>{room.name}</SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="rent_start_date">Mietbeginn</Label>
                    <Input
                      id="rent_start_date"
                      type="date"
                      value={editFormData.rent_start_date}
                      onChange={(e) => setEditFormData({ ...editFormData, rent_start_date: e.target.value })}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Adresse (für QR-Rechnung „Zahlbar durch")</Label>
                  <Input
                    placeholder="Strasse und Nr."
                    value={editFormData.street}
                    onChange={(e) => setEditFormData({ ...editFormData, street: e.target.value })}
                    data-testid="edit-rent-street-input"
                  />
                  <div className="grid grid-cols-3 gap-2">
                    <Input
                      placeholder="PLZ"
                      value={editFormData.postal_code}
                      onChange={(e) => setEditFormData({ ...editFormData, postal_code: e.target.value })}
                      data-testid="edit-rent-postal-input"
                    />
                    <Input
                      className="col-span-2"
                      placeholder="Ort"
                      value={editFormData.city}
                      onChange={(e) => setEditFormData({ ...editFormData, city: e.target.value })}
                      data-testid="edit-rent-city-input"
                    />
                  </div>
                  <p className="text-xs text-gray-500">Ohne PLZ + Ort bleibt „Zahlbar durch" auf der Rechnung leer.</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rent_first_billing_month">Erste Rechnung für Monat</Label>
                  <Input
                    id="rent_first_billing_month"
                    type="month"
                    value={editFormData.rent_first_billing_month}
                    onChange={(e) => setEditFormData({ ...editFormData, rent_first_billing_month: e.target.value })}
                    placeholder="z.B. 2026-02"
                  />
                  <p className="text-xs text-gray-500">
                    Wählen Sie den Monat, für den die erste Mietrechnung erstellt werden soll.
                    Folgerechnungen werden automatisch für den jeweiligen Monat erstellt.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Rechnungserstellung</Label>
                  <Select 
                    value={editFormData.rent_auto_invoice ? 'auto' : 'manual'} 
                    onValueChange={(value) => setEditFormData({ ...editFormData, rent_auto_invoice: value === 'auto' })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Automatisch (fester Betrag)</SelectItem>
                      <SelectItem value="manual">Manuell freigeben (variabler Betrag)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">
                    {editFormData.rent_auto_invoice 
                      ? 'Rechnung wird automatisch am 1. des Monats erstellt und versendet' 
                      : 'Rechnung wird 5 Tage vorher als Entwurf erstellt - Betrag anpassen und freigeben'}
                  </p>
                </div>
              </div>
            )}
            
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)} className="flex-1">
                Abbrechen
              </Button>
              <Button type="submit" className="flex-1">
                Aktualisieren
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Credentials Dialog - Shows login details after creating a new user */}
      <Dialog open={credentialsDialogOpen} onOpenChange={setCredentialsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              ✅ Benutzer erfolgreich erstellt!
            </DialogTitle>
            <DialogDescription>Login-Daten für den neuen Mitarbeiter</DialogDescription>
          </DialogHeader>
          {createdCredentials && (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-800 mb-2">
                  <strong>Bitte notieren oder weitergeben:</strong>
                </p>
                <div className="space-y-2 font-mono text-sm bg-white p-3 rounded border">
                  <p><strong>Name:</strong> {createdCredentials.name}</p>
                  <p><strong>E-Mail:</strong> {createdCredentials.email}</p>
                  <p><strong>Passwort:</strong> {createdCredentials.password}</p>
                  <p><strong>Rolle:</strong> {
                    createdCredentials.role === 'admin' ? 'Admin (Vollzugriff)' :
                    createdCredentials.role === 'trainer' ? 'Trainer (Termine + Ressourcen)' :
                    createdCredentials.role === 'mieter' ? 'Mieter (nur Ressourcen)' :
                    createdCredentials.role
                  }</p>
                </div>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Login-Link zum Weitergeben:</strong>
                </p>
                <p className="text-xs font-mono bg-white p-2 rounded border mt-2 break-all">
                  {window.location.origin}/login
                </p>
              </div>
              
              <Button 
                onClick={() => {
                  // Copy to clipboard
                  const text = `Login-Daten für ${createdCredentials.name}:\nE-Mail: ${createdCredentials.email}\nPasswort: ${createdCredentials.password}\nLogin: ${window.location.origin}/login`;
                  navigator.clipboard.writeText(text);
                  toast.success('Login-Daten in Zwischenablage kopiert!');
                }}
                className="w-full"
                variant="outline"
              >
                📋 Alle Daten kopieren
              </Button>
              
              <Button 
                onClick={() => setCredentialsDialogOpen(false)}
                className="w-full"
              >
                Schliessen
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Vacation/Ferien Dialog */}
      <Dialog open={vacationDialogOpen} onOpenChange={setVacationDialogOpen} modal={true}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              🏖️ Ferien verwalten - {selectedTrainer?.first_name} {selectedTrainer?.last_name}
            </DialogTitle>
            <DialogDescription>Urlaubs- und Abwesenheitszeiten eintragen</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            {/* Add New Vacation */}
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-semibold mb-4">Neue Ferien hinzufügen</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Von Datum</Label>
                    <Input
                      type="date"
                      value={vacationFormData.start_date}
                      onChange={(e) => setVacationFormData({...vacationFormData, start_date: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Bis Datum</Label>
                    <Input
                      type="date"
                      value={vacationFormData.end_date}
                      onChange={(e) => setVacationFormData({...vacationFormData, end_date: e.target.value})}
                    />
                  </div>
                </div>
                <div className="space-y-2 mt-4">
                  <Label>Notizen (optional)</Label>
                  <Input
                    value={vacationFormData.notes}
                    onChange={(e) => setVacationFormData({...vacationFormData, notes: e.target.value})}
                    placeholder="z.B. Sommerurlaub, Krankheit, etc."
                  />
                </div>
                <Button onClick={handleAddVacation} className="w-full mt-4">
                  Ferien hinzufügen
                </Button>
              </CardContent>
            </Card>
            
            {/* Existing Vacations List */}
            <div>
              <h3 className="font-semibold mb-4">Bestehende Ferien</h3>
              {vacations.length === 0 ? (
                <p className="text-gray-500 text-center py-8">Keine Ferien eingetragen</p>
              ) : (
                <div className="space-y-2">
                  {vacations.map((vacation) => (
                    <Card key={vacation.id}>
                      <CardContent className="p-4">
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-medium">
                              {new Date(vacation.start_date).toLocaleDateString('de-DE')} - {new Date(vacation.end_date).toLocaleDateString('de-DE')}
                            </p>
                            {vacation.notes && (
                              <p className="text-sm text-gray-600">{vacation.notes}</p>
                            )}
                          </div>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDeleteVacation(vacation.id)}
                          >
                            Löschen
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Permissions/Berechtigungen Dialog */}
      <Dialog open={permissionsDialogOpen} onOpenChange={setPermissionsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-purple-600" />
              Berechtigungen - {selectedTrainer?.first_name} {selectedTrainer?.last_name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            <p className="text-sm text-gray-600">
              Legen Sie fest, auf welche Bereiche dieser Trainer Zugriff hat.
            </p>
            
            {/* Group permissions by category */}
            {['Kalender', 'Kunden', 'Training & Ernährung', 'Finanzen', 'Berichte', 'Pakete', 'System'].map(category => {
              const categoryPermissions = Object.entries(PERMISSION_LABELS)
                .filter(([_, config]) => config.category === category);
              
              if (categoryPermissions.length === 0) return null;
              
              return (
                <Card key={category}>
                  <CardContent className="pt-4">
                    <h4 className="font-semibold text-sm text-gray-700 mb-3 flex items-center gap-2">
                      {category === 'Kalender' && <Calendar size={16} />}
                      {category === 'Kunden' && <Users size={16} />}
                      {category === 'Training & Ernährung' && <Dumbbell size={16} />}
                      {category === 'Finanzen' && <CreditCard size={16} />}
                      {category === 'Berichte' && <Eye size={16} />}
                      {category === 'Pakete' && <CreditCard size={16} />}
                      {category === 'System' && <Settings size={16} />}
                      {category}
                    </h4>
                    <div className="space-y-3">
                      {categoryPermissions.map(([key, config]) => (
                        <div key={key} className="flex items-center justify-between">
                          <Label htmlFor={key} className="text-sm cursor-pointer">
                            {config.label}
                          </Label>
                          <Switch
                            id={key}
                            checked={selectedPermissions[key] || false}
                            onCheckedChange={(checked) => handlePermissionChange(key, checked)}
                          />
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            
            <div className="flex gap-3 pt-4 border-t">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setPermissionsDialogOpen(false)} 
                className="flex-1"
              >
                Abbrechen
              </Button>
              <Button 
                onClick={handleSavePermissions}
                className="flex-1 bg-purple-600 hover:bg-purple-700"
              >
                <Save size={16} className="mr-2" />
                Speichern
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffPage;
